import "./SpaceOverview.css";

import { Button, Callout, Chip, Icons, KeyValue, LoadingState, Page, Panel, Stat } from "../../../../../ds";
import { IChangeRequestService, changeRequestService } from "../../../../changeRequests/services/ChangeRequestService";
import { ILockService, lockService } from "../../../../locks/services/LockService";
import { ISpaceService, spaceService } from "../../../services/SpaceService";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { useCallback, useEffect, useState } from "react";

import { IdentityUnavailableState } from "../../../../locks/components/LockStates";
import { SpaceState, fetchSpaceState } from "../../../utils/spaceState";
import { changeRequestsLabel } from "../../../utils/spaceFields";

interface ISpaceOverviewProps {
    /** Injected in tests; the shared clients otherwise. */
    spaces?: ISpaceService;
    locks?: ILockService;
    changeRequests?: IChangeRequestService;
}

interface Figures {
    pageCount?: number;
    pushPolicy?: "direct" | "propose";
    proposed?: number;
    locked?: number;
}

/**
 * Space overview (`spaces/:spaceId/overview`), folded into the Spaces home pattern (inventory notes): the space's title and
 * description as the page header, its figures (pages, push policy, proposed change requests, locked pages) as stat tiles that lead
 * to the screen behind each, and the identifiers. Read-only; the reads are the space, the facade's space, the proposed list and the
 * locks list.
 */
export function SpaceOverview(props: ISpaceOverviewProps) {
    const { spaceId } = useParams();
    const navigate = useNavigate();
    const spacesSvc = props.spaces ?? spaceService();
    const locksSvc = props.locks ?? lockService();
    const crSvc = props.changeRequests ?? changeRequestService();
    const [space, setSpace] = useState<SpaceState>({ status: "loading" });
    const [figures, setFigures] = useState<Figures>({});

    useEffect(() => {
        if (!spaceId) return;
        let alive = true;
        fetchSpaceState(spacesSvc, spaceId).then((next) => alive && setSpace(next));
        return () => {
            alive = false;
        };
    }, [spaceId, spacesSvc]);

    /** Try again: the loader, then the space. */
    const load = useCallback(() => {
        if (!spaceId) return;
        setSpace({ status: "loading" });
        fetchSpaceState(spacesSvc, spaceId).then(setSpace);
    }, [spaceId, spacesSvc]);

    useEffect(() => {
        if (!spaceId) return;
        let alive = true;
        const merge = (part: Figures) => alive && setFigures((current) => ({ ...current, ...part }));
        crSvc
            .getSpace(spaceId)
            .then((s) => merge({ pageCount: s.pageCount, pushPolicy: s.pushPolicy === "propose" ? "propose" : "direct" }))
            .catch(() => undefined);
        crSvc.list(spaceId, "proposed", 200).then((items) => merge({ proposed: items.length })).catch(() => undefined);
        locksSvc.listLocks(spaceId).then((list) => merge({ locked: list.length })).catch(() => undefined);
        return () => {
            alive = false;
        };
    }, [spaceId, crSvc, locksSvc]);

    if (space.status === "not-found") return <Navigate to="/notfound" replace />;

    const base = `/spaces/${spaceId}`;
    const loaded = space.status === "ready" ? space.space : undefined;

    let body;
    if (space.status === "loading") {
        body = <LoadingState text="Loading the space…" />;
    } else if (space.status === "identity-unavailable") {
        body = <IdentityUnavailableState onRetry={load} />;
    } else if (space.status === "error") {
        body = (
            <Callout tone="danger" title="The space could not be loaded">
                <p>{space.message}</p>
                <Button type="link" size="sm" text="Try again" onClick={load} />
            </Callout>
        );
    } else if (loaded) {
        body = (
            <div className="ark-stack is-lg">
                <div className="ark-overview-stats">
                    <Stat label="Pages" value={figures.pageCount} to={`${base}/pages`} hint="Open the page tree" />
                    <Stat
                        label="Push policy"
                        value={figures.pushPolicy}
                        to={`${base}/edit/space`}
                        hint={figures.pushPolicy === "propose" ? "Pushes wait for review" : figures.pushPolicy ? "Pushes apply at once" : undefined}
                    />
                    <Stat
                        label="Proposed"
                        value={figures.proposed}
                        tone={figures.proposed ? "warning" : undefined}
                        to={`${base}/change-requests`}
                        hint={figures.proposed ? `${changeRequestsLabel(figures.proposed)} waiting` : "Nothing waiting for review"}
                    />
                    <Stat label="Locked pages" value={figures.locked} to={`${base}/edit/permission`} hint="Only the locks you can read" />
                </div>
                <Panel title="About this space">
                    <KeyValue
                        columns={3}
                        items={[
                            { label: "Space id", value: <span className="ark-mono">{loaded.spaceId}</span>, id: "space-id" },
                            { label: "Project ID", value: loaded.projectId ? <span className="ark-mono">{loaded.projectId}</span> : undefined, id: "project-id" },
                            { label: "Highlighted", value: loaded.isHighlighted ? "Yes, listed first on the Spaces home" : "No", id: "highlighted" },
                        ]}
                    />
                </Panel>
            </div>
        );
    }

    return (
        <Page
            className="ark-overview-page"
            eyebrow="Space · Overview"
            status={loaded?.isHighlighted ? <Chip size="sm" tone="accent">Highlighted</Chip> : undefined}
            meta={spaceId ? `Space ${spaceId}` : undefined}
            title={loaded?.title ?? "Overview"}
            tabTitle={["Overview", loaded?.title]}
            intro={loaded?.description}
            actions={loaded ? (
                <>
                    <Button text="Settings" type="secondary" icon={<Icons.settings />} onClick={() => navigate(`${base}/edit/space`)} />
                    <Button text="Open pages" type="primary" icon={<Icons.fileText />} onClick={() => navigate(`${base}/pages`)} />
                </>
            ) : undefined}
        >
            {body}
        </Page>
    );
}
